import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { FeatureFlagNamesEnum } from '../../shared/constants/feature-flags.constants';
import { UserContextService } from '../services/user-context/user-context.service';

/** Options for {@link featureFlagGuard}. */
interface FeatureFlagGuardOptions {
  /**
   * When true, an acting ARC user bypasses the flag check and is granted access even when
   * the flag is off. **Opt-in and defaults to false** — the ARC override is for specific
   * internal use cases only, NOT the rollout default. Non-devteam ARC users should not see
   * in-progress features prematurely, so a new feature relies on its flag alone unless it
   * explicitly needs ARC staff to preview ahead of the flag.
   */
  allowActingArcUser?: boolean;
}

/**
 * Creates a route guard that controls access based on feature flag status.
 *
 * @param flagName - The name of the feature flag to check
 * @param behavior - Determines the guard behavior: 'show' allows access when flag is enabled,
 *                   'hide' denies access when flag is enabled. Defaults to 'show'.
 * @param options - See {@link FeatureFlagGuardOptions}. `allowActingArcUser` (default false)
 *                   opts a feature into the ARC-user flag bypass.
 * @returns A CanActivateFn that evaluates the feature flag and either permits navigation
 *          or redirects to the 404 page
 *
 * @example
 * ```typescript
 * // Flag-only (the default): hidden from everyone — including ARC staff — until the flag is on
 * canActivate: [featureFlagGuard(FeatureFlagNamesEnum.SHOW_REPORTS)]
 *
 * // Specific case: let acting ARC staff preview ahead of the flag
 * canActivate: [featureFlagGuard(FeatureFlagNamesEnum.SHOW_REPORTS, 'show', { allowActingArcUser: true })]
 * ```
 *
 * @throws Redirects to '/404' if the feature flag is not enabled (when behavior is 'show')
 *         or if the feature flag is enabled (when behavior is 'hide')
 */
export const featureFlagGuard = (
  flagName: FeatureFlagNamesEnum,
  behavior: 'show' | 'hide' = 'show',
  options: FeatureFlagGuardOptions = {},
): CanActivateFn => {
  return () => {
    const context = inject(UserContextService);
    const router = inject(Router);
    const flag = context.FeatureFlags()?.find(f => f.FeatureFlag === flagName);

    if ((flag?.FlagEnabled && behavior === 'show') || (options.allowActingArcUser && context.IsActingArcUser())) {
      return true; // Allow access
    } else {
      return router.createUrlTree(['/404']);
    }
  };
};